import { Close, Logout } from "@mui/icons-material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MailIcon from "@mui/icons-material/Mail";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Button } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import RefereeLogo from "assets/images/Referee_Logo_Full.png";
import { awsImageRootUrl } from "lib/constants";
import { Business } from "lib/features/business/types";
import { logOut, selectUser } from "lib/features/user/userSlice";
import { useAppDispatch, useAppSelector } from "lib/hooks";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import authServices from "services/auth-service";
import businessServices from "services/business-service";
import commentIcon from "../../../assets/icons/comment.png";
import { sideNavConfig, SideNavItem } from "./data";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { resetIsExistingBookingLink } from "lib/features/message/messageSlice";
import { NotificationsPopup } from "./NotificationsPopup";

export default function NavBar() {
  const user = useAppSelector(selectUser);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [businessData, setBusinessData] = useState<Business[]>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [userAvatar, setUserAvatar] = useState<string>("");

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState<{ [key: string]: boolean }>(
    {}
  );

  const toggleSubMenu = (title: string) => {
    setOpenSubMenus((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleMessagesClick = () => {
    navigate('/chat');
  };

  const handleLogout = () => {
    localStorage.clear();
    dispatch(logOut());
  };

  const handleProfileClick = () => {
    let profilePath = `/profile/${user?.id}`;
    if (user?.role_id === 3) {
      profilePath = `/profile/${user.id}`;
    } else if (user?.role_id === 4 && businessData) {
      profilePath = businessData?.[0]?.id
        ? `/business/${businessData?.[0]?.id}?showDetails=all`
        : `/profile/${user?.id}`;
    }
    navigate(profilePath);
  };

  const filteredSideNavConfig = sideNavConfig.filter((item: SideNavItem) => {
    if (user?.role_id === 3) {
      return (
        item.title !== "My Items" &&
        item.title !== "My Availabilities" &&
        item.title !== "Bookings & Referrals" &&
        item.title !== "Profile" &&
        item.title !== "Messages"
      );
    }
    if (user?.role_id === 4) {
      return item.title !== "Referrals" && item.title !== "Profile" && item.title !== "Messages";
    }

    return item.title !== "Profile" && item.title !== "Messages";
  });
  const getActualPath = (item: SideNavItem): string => {
    let actualPath = item.path;
    if (item.title === "Profile" && user?.id) {
      if (user.role_id === 3) {
        actualPath = `/profile/${user.id}`;
      } else if (user.role_id === 4 && businessData) {
        actualPath = businessData?.[0]?.id
          ? `/business/${businessData?.[0]?.id}?showDetails=all`
          : `/profile/${user?.id}`;
      }
    } else if (item.title === "Bookings & Referrals" && businessData) {
      actualPath = `/bookings/${businessData?.[0]?.id}`;
    } else if (item.title === "Referrals" && user) {
      actualPath = `/bookings/${user.id}`;
    } else if (item.title === "Availabilities" && businessData?.[0]?.id) {
      dispatch(resetIsExistingBookingLink());
      actualPath = `/availabilities/${businessData?.[0]?.id ? businessData?.[0]?.id : (user as any)?.id}`;
    } else if (item.title === "Availabilities" && !businessData?.[0]?.id) {
      dispatch(resetIsExistingBookingLink());
      actualPath = `/availabilities/${(user as any)?.id}`;
    } else if (item.title === "My Items") {
      dispatch(resetIsExistingBookingLink());
      actualPath = `/my-items`;
    } else if (item.title === "Share Multiple Businesses") {
      actualPath = `/businesslist/${user.id}`;
    } else if (item.title === "Account") {
      actualPath = `/profile/${user.id}`;
    } else if (item.title === "Reports") {
      if (user?.role_id === 3) {
        actualPath = `/reports/${user.id}`;
      } else if (user?.role_id === 4) {
        const businessId = businessData?.[0]?.id || (user as any)?.business_id;
        actualPath = `/reports/${businessId}`;
      }
    }
    return actualPath;
  };

  useEffect(() => {
    const fetchBusiness = async () => {
      if (!user?.id) return;
      try {
        const response = await businessServices.getUserBusiness(user.id);
        setBusinessData(response?.data);
        return response?.data;
      } catch (err) {
        console.log("error fetching business");
        return null;
      }
    };

    const fetchBusinessAvatar = async () => {
      if (!user?.id) return;
      try {
        const response = await fetchBusiness();
        const UserResponse = await authServices.fetchUser(user.id);

        const businessAvatar = response?.[0]?.businessImages?.[0]?.uri;
        const userAvatar = UserResponse?.data?.avatar;

        if (businessAvatar) {
          setUserAvatar(businessAvatar);
        } else if (userAvatar) {
          setUserAvatar(userAvatar);
        }
      } catch (err) {
        console.log("error fetching business or user avatar", err);
      }
    };
    fetchBusinessAvatar();
  }, [user?.id]);

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
      <MenuItem onClick={handleMenuClose}>My account</MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-search-account-menu-mobile";
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={handleMessagesClick}>
        <IconButton size="large" aria-label="messages" color="inherit">
          <MailIcon />
        </IconButton>
        <p>Messages</p>
      </MenuItem>
      <MenuItem onClick={handleNotificationOpen}>
        <IconButton
          size="large"
          aria-label="notifications"
          color="inherit"
        >
          <NotificationsIcon />
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={handleProfileClick}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  const renderSideMenu = () => (
    <Box sx={{ width: 336 }} role="presentation">
      <Button
        onClick={() => {
          setDrawerOpen(false);
        }}
        size="large"
        startIcon={<Close />}
        color="inherit"
        sx={{
          marginTop: "1.5rem",
          marginBottom: "0.5rem",
        }}
      />

      <List>
        {filteredSideNavConfig.map((item) => {
          const isSubMenuOpen = openSubMenus[item.title];
          const hasChildren = Array.isArray(item.children);

          return (
            <Box key={item.title}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (hasChildren) {
                      toggleSubMenu(item.title);
                    } else {
                      navigate(getActualPath(item));
                      setDrawerOpen(false);
                      setOpenSubMenus({});
                    }
                  }}
                  sx={{
                    padding: "10px 10px",
                  }}
                >
                  <ListItemIcon sx={{ color: "black" }}>
                    {typeof item.icon === "function" ? item.icon() : item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.title} sx={{ color: "black" }} />
                  {hasChildren &&
                    (isSubMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>

              {hasChildren && (
                <Collapse in={isSubMenuOpen} timeout="auto" unmountOnExit>
                  <List
                    component="div"
                    disablePadding
                    sx={{ marginLeft: "2rem" }}
                  >
                    {item?.children &&
                      item?.children?.length > 0 &&
                      item?.children.map((subItem: SideNavItem) => (
                        <ListItem key={subItem.title} disablePadding>
                          <ListItemButton
                            onClick={() => {
                              navigate(getActualPath(subItem));
                              setDrawerOpen(false);
                              setOpenSubMenus({});
                            }}
                            sx={{ paddingY: "8px" }}
                          >
                            <ListItemIcon sx={{ color: "black" }}>
                              {typeof subItem.icon === "function"
                                ? subItem.icon()
                                : subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.title}
                              sx={{ color: "black" }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>

      <List>
        <ListItem key={"text"} disablePadding sx={{ marginTop: "50px" }}>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon
              sx={{
                color: "black",
              }}
            >
              <Logout />
            </ListItemIcon>
            <ListItemText primary={"Logout"} sx={{ color: "black" }} />
          </ListItemButton>
        </ListItem>
      </List>
      <Box sx={{ margin: "50px 10px" }}>
        <Link to={"/home"}>
          <img src={RefereeLogo} height={"48px"} alt="" />
        </Link>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'sticky', top: 0, zIndex: 1100 }}>
      <AppBar
        position="static"
        sx={{
          background: "white",
          color: "black",
          boxShadow: "none",
          borderBottom: "1px solid #E0E0E0",
        }}
      >
        <Toolbar>
          {user?.id && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="open drawer"
              sx={{ mr: 2 }}
              onClick={() => {
                setDrawerOpen(!drawerOpen);
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Link to={"/home"}>
            <img src={RefereeLogo} height={"34px"} alt="" />
          </Link>
          <Box sx={{ flexGrow: 1 }} />
          {user?.id && (
            <>
              <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: "20px" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "black",
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <LocationOnIcon /> Charleston, SC
                </Typography>
                <IconButton
                  size="large"
                  aria-label="new notifications"
                  color="inherit"
                  onClick={handleNotificationOpen}
                  sx={{ padding: 0 }}
                >
                  <NotificationsIcon sx={{ fontSize: 30 }} />
                </IconButton>
                <IconButton
                  size="large"
                  aria-label="messages"
                  color="inherit"
                  onClick={handleMessagesClick}
                  sx={{ padding: 0 }}
                >
                  <img src={commentIcon} alt="" height="30px" width="30px" />
                </IconButton>
                <IconButton
                  size="large"
                  edge="end"
                  aria-label="account of current user"
                  onClick={handleProfileClick}
                  color="inherit"
                  sx={{ padding: 0, marginLeft: "12px" }}
                >
                  {userAvatar ? (
                    <Avatar
                      src={`${awsImageRootUrl}/${userAvatar}`}
                      alt="Profile"
                      sx={{
                        width: 40,
                        height: 40,
                      }}
                    />
                  ) : (
                    <AccountCircle sx={{ fontSize: 40 }} />
                  )}
                </IconButton>
              </Box>
            </>
          )}
        </Toolbar>
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: "20px", justifyContent: 'flex-end', marginBottom: "14px" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "black",
              display: "flex",
              alignItems: "center", marginRight: "auto", marginLeft: "20px"
            }}
          >
            <LocationOnIcon /> Charleston, SC
          </Typography>
          <IconButton
            size="large"
            aria-label="new notifications"
            color="inherit"
            onClick={handleNotificationOpen}
            sx={{ padding: 0 }}
          >
            <NotificationsIcon sx={{ fontSize: 30 }} />
          </IconButton>
          <IconButton
            size="large"
            aria-label="messages"
            color="inherit"
            onClick={handleMessagesClick}
            sx={{ padding: 0 }}
          >
            <img src={commentIcon} alt="" height="30px" width="30px" />
          </IconButton>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            onClick={handleProfileClick}
            color="inherit"
            sx={{ padding: 0, marginLeft: "12px", marginRight: "20px" }}
          >
            {userAvatar ? (
              <Avatar
                src={`${awsImageRootUrl}/${userAvatar}`}
                alt="Profile"
                sx={{
                  width: 40,
                  height: 40,
                }}
              />
            ) : (
              <AccountCircle sx={{ fontSize: 40 }} />
            )}
          </IconButton>
        </Box>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {renderSideMenu()}
      </Drawer>
      <NotificationsPopup
        anchorEl={notificationAnchorEl}
        onClose={handleNotificationClose}
      />
    </Box>
  );
}